import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MessageCircle, Phone, ArrowUpRight, Loader2, Clock, Users, Youtube } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatHour = (h) => {
  if (h == null) return '—';
  const suffix = h >= 12 ? 'PM' : 'AM';
  const d = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${d}:00 ${suffix}`;
};

const formatDateHeader = (iso) => {
  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  if (iso === today) return 'Today';
  if (iso === tomorrow) return 'Tomorrow';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
};

const cleanPhone = (p) => (p || '').replace(/\D/g, '');

const TodayScheduleWidget = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ dates: [], appointments: [], count: 0 });

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const res = await axios.get(`${API}/admin/appointments/upcoming`, {
          params: { days: 2 },
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch {
        setData({ dates: [], appointments: [], count: 0 });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group by date
  const byDate = {};
  for (const d of data.dates) byDate[d] = [];
  for (const apt of data.appointments) {
    if (!byDate[apt.date]) byDate[apt.date] = [];
    byDate[apt.date].push(apt);
  }

  return (
    <section data-testid="today-schedule-widget" style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', padding: 22, marginBottom: 32 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: '#e0f2fe', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Calendar size={16} />
            </div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>Upcoming Schedule</h2>
            {!loading && (
              <span data-testid="ts-count-badge" style={{ marginLeft: 4, background: '#f3f4f6', color: '#4b5563', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999 }}>
                {data.count}
              </span>
            )}
          </div>
          <p style={{ margin: '6px 0 0 40px', fontSize: 12, color: '#6b7280' }}>Next 2 days · DigiMitra visits + YouTuber calls</p>
        </div>
        <Link
          to="/admin/appointments"
          data-testid="ts-view-all"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#0f172a', textDecoration: 'none', padding: '8px 12px', borderRadius: 9, border: '1px solid #e5e7eb', transition: 'all 0.15s' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          Manage slots <ArrowUpRight size={13} />
        </Link>
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#9ca3af', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Loader2 size={16} style={{ animation: 'adm-spin 1s linear infinite' }} /> Loading schedule...
        </div>
      ) : data.count === 0 ? (
        <div data-testid="ts-empty" style={{ padding: '32px 20px', textAlign: 'center', background: '#f9fafb', borderRadius: 12 }}>
          <Calendar size={28} style={{ color: '#d1d5db', margin: '0 auto 8px', display: 'block' }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>No appointments scheduled</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>When someone books through the site, it'll appear here.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {data.dates.map((d) => {
            const items = byDate[d] || [];
            return (
              <div key={d} data-testid={`ts-day-${d}`}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>{formatDateHeader(d)}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{items.length} appointment{items.length === 1 ? '' : 's'}</span>
                </div>
                {items.length === 0 ? (
                  <div style={{ padding: '10px 14px', background: '#fafafa', borderRadius: 10, fontSize: 12, color: '#9ca3af' }}>Nothing booked.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {items.map((a) => {
                      const isYt = a.service === 'youtuber';
                      const ServiceIcon = isYt ? Youtube : Users;
                      const tint = isYt ? '#faf5ff' : '#fff0f5';
                      const fg = isYt ? '#7c3aed' : '#e5266a';
                      const whatsappPhone = cleanPhone(a.phone);
                      const dateLabel = formatDateHeader(d);
                      const whenPhrase = (dateLabel === 'Today' || dateLabel === 'Tomorrow') ? dateLabel.toLowerCase() : `on ${dateLabel}`;
                      const whatsappMsg = encodeURIComponent(
                        `Hi ${a.name}, this is Elderra. Just confirming your ${a.service_label} ${whenPhrase} at ${formatHour(a.hour)}. See you soon!`
                      );
                      return (
                        <div
                          key={a.id}
                          data-testid={`ts-row-${a.id}`}
                          style={{ display: 'grid', gridTemplateColumns: '88px 1fr auto', gap: 12, alignItems: 'center', padding: '12px 14px', background: '#fafafa', borderRadius: 10, border: '1px solid #f3f4f6' }}
                        >
                          {/* Time */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                            <Clock size={13} style={{ color: '#9ca3af' }} />
                            {formatHour(a.hour)}
                          </div>
                          {/* Name + service */}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{a.name}</span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', background: tint, color: fg, padding: '2px 8px', borderRadius: 999 }}>
                                <ServiceIcon size={10} /> {a.service_label}
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span>{a.detail}</span>
                              {a.city && <><span style={{ color: '#d1d5db' }}>·</span><span>{a.city}</span></>}
                              {a.phone && <><span style={{ color: '#d1d5db' }}>·</span><span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Phone size={10} /> {a.phone}</span></>}
                            </div>
                          </div>
                          {/* Actions */}
                          <div style={{ display: 'flex', gap: 6 }}>
                            {whatsappPhone.length >= 10 && (
                              <a
                                href={`https://wa.me/${whatsappPhone.length === 10 ? '91' + whatsappPhone : whatsappPhone}?text=${whatsappMsg}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`ts-whatsapp-${a.id}`}
                                title="Send WhatsApp confirmation"
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: '#25D366', color: '#fff', textDecoration: 'none', transition: 'transform 0.15s' }}
                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                              >
                                <MessageCircle size={15} />
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <style>{`@keyframes adm-spin { to { transform: rotate(360deg); } }`}</style>
    </section>
  );
};

export default TodayScheduleWidget;
